import { addMonths, differenceInMonths, format, isBefore, parseISO } from 'date-fns';
import { supabase } from './supabase';
import {
    calculateInvoiceTotals,
    createInvoice,
    type CalculatedInvoiceTotals,
    type InvoiceItemInput,
    type InvoicePayload,
} from './invoiceUtils';
import { DEFAULT_NO_VAT_FORFETTARIO_CLAUSE } from './noVatClause';
import type { Database } from '../types/database.types';

type InvoiceRow = Database['public']['Tables']['invoices']['Row'];

/** Range di mesi fatturabili in una sola fattura di abbonamento. */
export const MIN_SUBSCRIPTION_INVOICE_MONTHS = 1;
export const MAX_SUBSCRIPTION_INVOICE_MONTHS = 12;
export const SUBSCRIPTION_MONTH_OPTIONS = Array.from(
    { length: MAX_SUBSCRIPTION_INVOICE_MONTHS - MIN_SUBSCRIPTION_INVOICE_MONTHS + 1 },
    (_, i) => i + MIN_SUBSCRIPTION_INVOICE_MONTHS
);

export const SUBSCRIPTION_INVOICE_VAT_RATE = 22;
export const SUBSCRIPTION_INVOICE_UNIT = 'mese';

/** Cadenze di fatturazione proposte sul contratto (ogni quanti mesi si fattura). */
export const BILLING_PERIOD_OPTIONS: { months: number; label: string }[] = [
    { months: 1, label: 'Mensile' },
    { months: 3, label: 'Trimestrale' },
    { months: 6, label: 'Semestrale' },
    { months: 12, label: 'Annuale' },
];

export const getBillingPeriodLabel = (months: number | null | undefined): string =>
    BILLING_PERIOD_OPTIONS.find((o) => o.months === months)?.label ?? `Ogni ${months ?? 1} mesi`;

export interface SubscriptionInvoicePeriod {
    /** Primo giorno del periodo fatturato (ISO yyyy-MM-dd). */
    start: string;
    /** Primo giorno NON coperto dalla fattura = nuova data di rinnovo (ISO yyyy-MM-dd). */
    end: string;
}

/** Subset di customer_subscriptions necessario per fatturare. */
export interface InvoiceableSubscription {
    id: string;
    customer_id: string;
    monthly_amount: number;
    next_renewal_date: string;
}

const clampMonths = (months: number): number =>
    Math.min(MAX_SUBSCRIPTION_INVOICE_MONTHS, Math.max(MIN_SUBSCRIPTION_INVOICE_MONTHS, Math.round(months)));

const formatItalianDate = (isoDate: string): string => format(parseISO(isoDate), 'dd/MM/yyyy');

/**
 * Periodo coperto da una fattura di N mesi a partire da `periodStart`.
 * `end` è esclusivo: coincide con la nuova data di rinnovo.
 */
export const getSubscriptionInvoicePeriod = (
    periodStart: string,
    months: number
): SubscriptionInvoicePeriod => ({
    start: periodStart,
    end: format(addMonths(parseISO(periodStart), clampMonths(months)), 'yyyy-MM-dd'),
});

/**
 * Mesi suggeriti per una subscription scaduta: quanti ne servono per coprire
 * il periodo scoperto fino a oggi (min 1, max 12).
 */
export const getSuggestedInvoiceMonths = (
    nextRenewalDate: string,
    today: Date = new Date()
): number => {
    const renewal = parseISO(nextRenewalDate);
    const fullMonths = differenceInMonths(today, renewal);
    if (fullMonths < 0) return MIN_SUBSCRIPTION_INVOICE_MONTHS;
    const covered = addMonths(renewal, fullMonths);
    return clampMonths(isBefore(covered, today) ? fullMonths + 1 : fullMonths);
};

/** Riga fattura per N mesi di abbonamento. */
export const buildSubscriptionInvoiceItems = (
    subscription: InvoiceableSubscription,
    months: number,
    period: SubscriptionInvoicePeriod,
    vatRate: number = SUBSCRIPTION_INVOICE_VAT_RATE
): InvoiceItemInput[] => [
    {
        product_id: null,
        description: `Abbonamento servizi ${months} ${months === 1 ? 'mese' : 'mesi'} - periodo dal ${formatItalianDate(period.start)} al ${formatItalianDate(period.end)}`,
        quantity: months,
        unit: SUBSCRIPTION_INVOICE_UNIT,
        unit_price: subscription.monthly_amount,
        discount_percentage: 0,
        vat_rate: vatRate,
    },
];

/** Anteprima totali (imponibile, IVA, marca da bollo) senza scrivere su DB. */
export const previewSubscriptionInvoiceTotals = (
    subscription: InvoiceableSubscription,
    months: number,
    period: SubscriptionInvoicePeriod,
    noVatForfettario: boolean
): CalculatedInvoiceTotals =>
    calculateInvoiceTotals(
        buildSubscriptionInvoiceItems(subscription, months, period),
        noVatForfettario
    );

export interface CreateSubscriptionInvoiceParams {
    subscription: InvoiceableSubscription;
    months: number;
    /** Inizio periodo fatturato (ISO yyyy-MM-dd). Default: data di rinnovo corrente. */
    periodStart?: string;
    invoiceDate: string;
    status?: 'draft' | 'issued';
    noVatForfettario?: boolean;
    noVatClauseText?: string | null;
    iban?: string | null;
    /** Se true, sposta `next_renewal_date` alla fine del periodo fatturato. */
    advanceRenewal?: boolean;
}

export interface CreateSubscriptionInvoiceResult {
    invoice: InvoiceRow;
    period: SubscriptionInvoicePeriod;
    renewalAdvanced: boolean;
    /** Valorizzato solo se la fattura è stata creata ma l'aggiornamento rinnovo è fallito. */
    renewalError?: string;
}

/**
 * Crea una fattura per N mesi (1-12) di una subscription e, opzionalmente,
 * sposta in avanti la data di prossimo rinnovo.
 */
export const createSubscriptionInvoice = async (
    params: CreateSubscriptionInvoiceParams
): Promise<CreateSubscriptionInvoiceResult> => {
    const { subscription, months, invoiceDate } = params;

    if (!Number.isInteger(months) || months < MIN_SUBSCRIPTION_INVOICE_MONTHS || months > MAX_SUBSCRIPTION_INVOICE_MONTHS) {
        throw new Error(`I mesi da fatturare devono essere tra ${MIN_SUBSCRIPTION_INVOICE_MONTHS} e ${MAX_SUBSCRIPTION_INVOICE_MONTHS}.`);
    }
    if (!subscription.customer_id) {
        throw new Error('Subscription senza cliente associato.');
    }
    if (!(subscription.monthly_amount > 0)) {
        throw new Error('Importo mensile non valido: impossibile fatturare.');
    }

    const period = getSubscriptionInvoicePeriod(
        params.periodStart || subscription.next_renewal_date,
        months
    );
    const noVatForfettario = params.noVatForfettario !== false;

    const payload: InvoicePayload = {
        customer_id: subscription.customer_id,
        invoice_date: invoiceDate,
        status: params.status ?? 'issued',
        document_type: 'invoice',
        items: buildSubscriptionInvoiceItems(subscription, months, period),
        no_vat_forfettario: noVatForfettario,
        no_vat_clause_text: noVatForfettario
            ? (params.noVatClauseText ?? DEFAULT_NO_VAT_FORFETTARIO_CLAUSE)
            : null,
        iban: params.iban ?? null,
        causale: `Abbonamento dal ${formatItalianDate(period.start)} al ${formatItalianDate(period.end)}`,
        subscription_id: subscription.id,
        subscription_period_start: period.start,
        subscription_period_end: period.end,
    };

    let invoice: InvoiceRow;
    try {
        invoice = await createInvoice(payload);
    } catch (err: any) {
        // Indice unico (subscription_id, subscription_period_start): periodo già coperto
        if (err?.code === '23505') {
            throw new Error(
                `Il periodo dal ${formatItalianDate(period.start)} è già stato fatturato per questo abbonamento.`
            );
        }
        throw err;
    }

    if (params.advanceRenewal === false) {
        return { invoice, period, renewalAdvanced: false };
    }

    const { error } = await (supabase.from('customer_subscriptions') as any)
        .update({ next_renewal_date: period.end })
        .eq('id', subscription.id);

    if (error) {
        return {
            invoice,
            period,
            renewalAdvanced: false,
            renewalError: error.message ?? 'Errore aggiornamento data rinnovo',
        };
    }

    return { invoice, period, renewalAdvanced: true };
};

/** Bozza generata da un abbonamento, in attesa di controllo ed emissione. */
export interface SubscriptionDraftInvoice {
    id: string;
    invoice_date: string;
    total_amount: number;
    subscription_period_start: string | null;
    subscription_period_end: string | null;
    created_at: string;
    customers: { company_name: string } | null;
}

const DRAFT_SELECT =
    'id, invoice_date, total_amount, subscription_period_start, subscription_period_end, created_at, customers(company_name)';

/** Bozze di abbonamento da lavorare, dalla più vecchia. */
export const fetchSubscriptionDraftInvoices = async (): Promise<SubscriptionDraftInvoice[]> => {
    const { data, error } = await supabase
        .from('invoices')
        .select(DRAFT_SELECT)
        .eq('status', 'draft')
        .not('subscription_id', 'is', null)
        .order('subscription_period_start', { ascending: true });

    if (error) throw error;
    return (data ?? []) as unknown as SubscriptionDraftInvoice[];
};

/** Bozze di abbonamento create dopo un certo istante (per l'avviso in app). */
export const fetchSubscriptionDraftsCreatedAfter = async (
    sinceISO: string
): Promise<SubscriptionDraftInvoice[]> => {
    const { data, error } = await supabase
        .from('invoices')
        .select(DRAFT_SELECT)
        .eq('status', 'draft')
        .not('subscription_id', 'is', null)
        .gt('created_at', sinceISO)
        .order('created_at', { ascending: true });

    if (error) throw error;
    return (data ?? []) as unknown as SubscriptionDraftInvoice[];
};

export interface GeneratedSubscriptionDraft {
    subscription_id: string;
    invoice_id: string;
    customer_id: string;
    period_start: string;
    period_end: string;
    total_amount: number;
}

/**
 * Lancia subito la generazione delle bozze (stessa funzione del job notturno).
 * `leadDays`: quanti giorni di anticipo considerare sull'inizio del periodo.
 */
export const generateSubscriptionDraftInvoices = async (
    leadDays: number = 7
): Promise<GeneratedSubscriptionDraft[]> => {
    const { data, error } = await supabase.rpc('generate_subscription_draft_invoices', {
        p_lead_days: leadDays,
    });

    if (error) throw error;
    return (data ?? []) as unknown as GeneratedSubscriptionDraft[];
};

export interface AutoInvoiceStatus {
    job_active: boolean;
    schedule: string;
    last_run: string | null;
    last_status: string | null;
}

/** Stato del job notturno: acceso? quando ha girato l'ultima volta? */
export const fetchAutoInvoiceStatus = async (): Promise<AutoInvoiceStatus | null> => {
    const { data, error } = await supabase.rpc('subscription_autoinvoice_status');
    if (error) return null;
    const rows = (data ?? []) as unknown as AutoInvoiceStatus[];
    return rows[0] ?? null;
};
